// Super-user "view as" picker (slice 13.a.3.2). Lists impersonatable accounts
// (GET /auth/impersonate/targets) and starts a READ-ONLY overlay on click —
// POST /auth/impersonate then refreshUser() so the whole SPA re-renders as the
// target. Exit is via the oxblood ImpersonationBanner (shell.tsx), shown on
// every page while the overlay is active. RequireSuperUser-gated in App.tsx.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '../../components/shell';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

interface Target {
  id: string;
  email: string;
  display_name: string;
  role: string;
  tenant_id: string;
  tenant_name: string;
  client_legal_name: string;
}

export default function Impersonate() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Target[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [startingId, setStartingId] = useState<string | null>(null);
  // Defensive only: under the backend contract this page is unreachable while
  // impersonating (the overlay makes request.user the non-super target, so
  // RequireSuperUser redirects + the nav entry hides). Kept as belt-and-braces.
  const alreadyImpersonating = !!user?.impersonated_by;

  useEffect(() => {
    let cancelled = false;
    api<Target[]>('/auth/impersonate/targets')
      .then(d => { if (!cancelled) setRows(d); })
      .catch((e: any) => { if (!cancelled) setErr(e?.payload?.detail || 'Failed to load accounts.'); });
    return () => { cancelled = true; };
  }, []);

  const viewAs = async (t: Target) => {
    setErr(null);
    setStartingId(t.id);
    let started = false;
    try {
      await api('/auth/impersonate', { method: 'POST', body: { target_user_id: t.id } });
      started = true;
      await refreshUser();
      navigate('/dashboard', { replace: true });
    } catch (e: any) {
      if (started) {
        // The overlay IS live server-side but the SPA couldn't refresh /auth/me.
        // Never leave client state desynced (no banner + mysterious 403s);
        // hard-reload so AuthProvider re-probes the true session.
        window.location.assign('/dashboard');
        return;
      }
      setStartingId(null);
      setErr(e?.payload?.detail || 'Could not start the view-as session.');
    }
  };

  return (
    <div className="view container">
      <PageHeader
        kicker="ADMIN · VIEW AS"
        title={<><em>View as</em> an account.</>}
        meta={<span className="label-s">READ-ONLY · EVERY SESSION AUDITED</span>}
      />

      <p className="serif-body" style={{ color: 'var(--ink-2)', maxWidth: '62ch', marginBottom: 32 }}>
        See the portal exactly as a fund manager or client sees it. The session is
        read-only — you cannot change their data while viewing — and every start
        and exit is recorded in the audit log.
      </p>

      {alreadyImpersonating && (
        <p style={{
          fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: 'var(--oxblood)', marginBottom: 24,
        }}>
          You are currently viewing as another account. Exit that session (top banner) before starting another.
        </p>
      )}

      {err && (
        <p style={{
          fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: 'var(--oxblood)', marginBottom: 24,
        }}>
          {err}
        </p>
      )}

      {rows && rows.length === 0 && (
        <p className="serif-body" style={{ color: 'var(--ink-3)' }}>No accounts available to view as.</p>
      )}

      {rows && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--ink)' }}>
              <Th>Account</Th>
              <Th>Role</Th>
              <Th>Firm</Th>
              <Th align="right">Action</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map(t => (
              <tr key={t.id} style={{ borderBottom: 'var(--hairline)' }}>
                <Td>
                  <span style={{ fontFamily: 'var(--serif)', fontSize: 18 }}><em>{t.display_name}</em></span>
                  <div style={{
                    fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.14em',
                    textTransform: 'uppercase', color: 'var(--ink-3)', marginTop: 4,
                  }}>
                    {t.email}{t.client_legal_name ? ` · ${t.client_legal_name}` : ''}
                  </div>
                </Td>
                <Td>
                  <span style={{
                    fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em',
                    textTransform: 'uppercase', color: 'var(--ink-2)',
                  }}>
                    {t.role}
                  </span>
                </Td>
                <Td>
                  <span style={{
                    fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em',
                    textTransform: 'uppercase', color: 'var(--ink-3)',
                  }}>
                    {t.tenant_name}
                  </span>
                </Td>
                <Td align="right">
                  <button
                    className="btn btn-ghost"
                    disabled={alreadyImpersonating || startingId !== null}
                    onClick={() => viewAs(t)}
                  >
                    {startingId === t.id ? 'Starting…' : 'View as'}
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

function Th({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'right' }) {
  return (
    <th style={{
      textAlign: align, padding: '12px 16px',
      fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.16em',
      textTransform: 'uppercase', color: 'var(--ink-3)', fontWeight: 400,
    }}>
      {children}
    </th>
  );
}
function Td({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'right' }) {
  return (
    <td style={{ textAlign: align, padding: '20px 16px', verticalAlign: 'top' }}>
      {children}
    </td>
  );
}
